/**
 * @module KatapultModelUpdateComposers
 * A utility module that contains update composers for the KatapultModel module.
 * These can be used outside of KatapultModel.
 */

/**
 * An internal function to compose an update object to update a model path.
 * @param {string} path - The model path to update.
 * @param {any} value - The new value of the path.
 * @returns {object} The composed update object.
 */
export function composeModelPathUpdate(path, value) {
  // Compose the update object
  const update = {
    [`${path}`]: value
  };

  return update;
}

/**
 * Compose an update object to update the model read/write access for a company.
 * @param {object} companyAccessObject - An object where the keys are companyIds and the values are booleans representing whether the company should have access to the model.
 * @param {object} data - The data needed to compose the update object.
 * @param {string} data.modelId - The id of the model.
 * @param {string} data.type - The type of access to update, either 'read', 'use', or 'write'.
 * @returns {object} The composed update object.
 */
export function composeCompaniesWithAccessUpdate(companyAccessObject, data) {
  const { type } = data;

  if (!['read', 'write', 'use'].includes(type)) throw new Error(`Invalid access type: ${type}`);

  const update = {};
  for (const [companyId, canAccess] of Object.entries(companyAccessObject)) {
    let path;
    if (type === 'read') {
      path = `companies_with_model_access/${companyId}`;
      Object.assign(update, composeModelPathUpdate(path, canAccess === true ? true : null));
    } else if (type === 'write') {
      path = `companies_with_write_model_access/${companyId}`;
      Object.assign(update, composeModelPathUpdate(path, canAccess === true ? true : null));
    } else if (type === 'use') {
      path = `companies_with_use_model_access/${companyId}`;
      Object.assign(update, composeModelPathUpdate(path, canAccess === true ? true : null));
    }
  }
  return update;
}
